import type {
  DashboardCardConfig,
  DashboardCardItem,
  DashboardWidget,
  Report,
  ReportTag,
} from '@common/types';

export const ALL_TAGS: ReportTag[] = [
  { id: 'default', name: 'Default', color: '#00a0dc', description: 'Predefined system reports' },
  {
    id: 'finance',
    name: 'Finance',
    color: '#22c55e',
    description: 'Financial reports and revenue data',
  },
  {
    id: 'guests',
    name: 'Guests',
    color: '#14b8a6',
    description: 'Guest activity and satisfaction metrics',
  },
  {
    id: 'monthly',
    name: 'Monthly',
    color: '#3b82f6',
    description: 'Reports generated on a monthly cadence',
  },
  {
    id: 'occupancy',
    name: 'Occupancy',
    color: '#6366f1',
    description: 'Room occupancy rates and patterns',
  },
  {
    id: 'operations',
    name: 'Operations',
    color: '#a855f7',
    description: 'Operational efficiency and task tracking',
  },
  {
    id: 'performance',
    name: 'Performance',
    color: '#f97316',
    description: 'Performance KPIs and benchmarks',
  },
  { id: 'revenue', name: 'Revenue', color: '#ec4899', description: 'Revenue trends and forecasts' },
  {
    id: 'staff',
    name: 'Staff',
    color: '#64748b',
    description: 'Staff productivity and scheduling',
  },
];

const DEFAULT_TAG = ALL_TAGS[0];

const REPORT_TAGS: ReportTag[][] = [
  [ALL_TAGS[3], ALL_TAGS[2]],
  [ALL_TAGS[5], ALL_TAGS[8]],
  [ALL_TAGS[7], ALL_TAGS[1]],
  [ALL_TAGS[8], ALL_TAGS[6]],
  [ALL_TAGS[4], ALL_TAGS[3]],
  [ALL_TAGS[2], ALL_TAGS[6]],
  [ALL_TAGS[5]],
  [ALL_TAGS[7], ALL_TAGS[3], ALL_TAGS[1]],
];

const REPORT_NAMES = [
  'Monthly Guest Analytics',
  'Service Ticket Performance',
  'Revenue Dashboard',
  'Staff Efficiency Report',
  'Occupancy Trends',
  'Guest Satisfaction Analysis',
  'Maintenance Schedule Overview',
  'Booking Forecast',
];

const REPORT_DESCRIPTIONS = [
  'Comprehensive overview of guest metrics and trends',
  'Track service ticket resolution times and status',
  'Revenue performance across all properties',
  'Staff productivity and performance metrics',
  'Room occupancy rates and patterns',
  'Guest feedback and satisfaction scores',
  'Maintenance tasks and schedules',
  'Upcoming bookings and forecast data',
];

const card = (
  id: string,
  heading: string,
  items: ReadonlyArray<DashboardCardItem>,
  colSpan: number,
  rowSpan: number,
): DashboardWidget => ({
  id,
  kind: 'card',
  config: { heading, items } as DashboardCardConfig,
  colSpan,
  rowSpan,
});

const metric = (id: string, metricKey: string): DashboardCardItem =>
  ({ kind: 'metric', id, metricKey }) as DashboardCardItem;

const chart = (id: string, sourceKey: string, chartType: string): DashboardCardItem =>
  ({ kind: 'chart', id, sourceKey, chartType }) as DashboardCardItem;

const REPORT_WIDGET_LAYOUTS: DashboardWidget[][] = [
  [
    card('l0-c1', '', [metric('l0-c1-i1', 'grr')], 3, 2),
    card('l0-c2', '', [metric('l0-c2-i1', 'survey-review-rating')], 3, 2),
    card('l0-c3', '', [metric('l0-c3-i1', 'total-reviews')], 3, 2),
    card('l0-c4', '', [metric('l0-c4-i1', 'response-rate')], 3, 2),
    card('l0-c5', 'Guest Trends', [chart('l0-c5-i1', 'trend-analysis', 'line')], 8, 5),
    card('l0-c6', 'Net Promoter Score', [chart('l0-c6-i1', 'net-promoter-score', 'line')], 4, 5),
  ],
  [
    card('l1-c1', '', [metric('l1-c1-i1', 'open-tickets')], 4, 2),
    card('l1-c2', '', [metric('l1-c2-i1', 'resolved-tickets')], 4, 2),
    card('l1-c3', '', [metric('l1-c3-i1', 'response-rate')], 4, 2),
    card('l1-c4', 'Service Ratings', [chart('l1-c4-i1', 'service-ratings', 'bar')], 6, 5),
    card('l1-c5', 'Sentiment', [chart('l1-c5-i1', 'sentiment', 'donut')], 6, 5),
  ],
  [
    card('l2-c1', '', [metric('l2-c1-i1', 'grr')], 3, 2),
    card('l2-c2', '', [metric('l2-c2-i1', 'online-review-rating')], 3, 2),
    card('l2-c3', '', [metric('l2-c3-i1', 'compset-position')], 3, 2),
    card('l2-c4', '', [metric('l2-c4-i1', 'nps-score')], 3, 2),
    card('l2-c5', 'Trend Analysis', [chart('l2-c5-i1', 'trend-analysis', 'area')], 8, 5),
    card('l2-c6', 'Competitor Performance', [chart('l2-c6-i1', 'competitor', 'bar')], 4, 5),
  ],
  [
    card('l3-c1', '', [metric('l3-c1-i1', 'open-tickets')], 3, 2),
    card('l3-c2', '', [metric('l3-c2-i1', 'resolved-tickets')], 3, 2),
    card('l3-c3', '', [metric('l3-c3-i1', 'response-rate')], 3, 2),
    card('l3-c4', '', [metric('l3-c4-i1', 'sentiment-score')], 3, 2),
    card('l3-c5', 'Service Ratings', [chart('l3-c5-i1', 'service-ratings', 'bar')], 8, 5),
    card('l3-c6', 'Sentiment', [chart('l3-c6-i1', 'sentiment', 'donut')], 4, 5),
  ],
  [
    card('l4-c1', '', [metric('l4-c1-i1', 'grr')], 4, 2),
    card('l4-c2', '', [metric('l4-c2-i1', 'online-review-rating')], 4, 2),
    card('l4-c3', '', [metric('l4-c3-i1', 'compset-position')], 4, 2),
    card('l4-c4', 'Trend Analysis', [chart('l4-c4-i1', 'trend-analysis', 'area')], 8, 5),
    card('l4-c5', 'Review Source Volume', [chart('l4-c5-i1', 'review-source-volume', 'bar')], 4, 5),
  ],
  [
    card('l5-c1', '', [metric('l5-c1-i1', 'nps-score')], 3, 2),
    card('l5-c2', '', [metric('l5-c2-i1', 'sentiment-score')], 3, 2),
    card('l5-c3', '', [metric('l5-c3-i1', 'total-reviews')], 3, 2),
    card('l5-c4', '', [metric('l5-c4-i1', 'response-rate')], 3, 2),
    card('l5-c5', 'Net Promoter Score', [chart('l5-c5-i1', 'net-promoter-score', 'line')], 8, 4),
    card('l5-c6', 'Sentiment', [chart('l5-c6-i1', 'sentiment', 'donut')], 4, 4),
  ],
  [
    card('l6-c1', '', [metric('l6-c1-i1', 'open-tickets')], 4, 2),
    card('l6-c2', '', [metric('l6-c2-i1', 'resolved-tickets')], 4, 2),
    card('l6-c3', '', [metric('l6-c3-i1', 'total-reviews')], 4, 2),
    card('l6-c4', 'Service Ratings', [chart('l6-c4-i1', 'service-ratings', 'bar')], 6, 5),
    card('l6-c5', 'Trend Analysis', [chart('l6-c5-i1', 'trend-analysis', 'line')], 6, 5),
  ],
  [
    card('l7-c1', '', [metric('l7-c1-i1', 'grr')], 3, 2),
    card('l7-c2', '', [metric('l7-c2-i1', 'survey-review-rating')], 3, 2),
    card('l7-c3', '', [metric('l7-c3-i1', 'nps-score')], 3, 2),
    card('l7-c4', '', [metric('l7-c4-i1', 'total-reviews')], 3, 2),
    card('l7-c5', 'Trend Analysis', [chart('l7-c5-i1', 'trend-analysis', 'line')], 8, 5),
    card('l7-c6', 'Review Source Volume', [chart('l7-c6-i1', 'review-source-volume', 'bar')], 4, 5),
  ],
];

const PREDEFINED_LAYOUTS: DashboardWidget[][] = [
  [
    card('p0-c1', '', [metric('p0-c1-i1', 'grr')], 3, 2),
    card('p0-c2', '', [metric('p0-c2-i1', 'nps-score')], 3, 2),
    card('p0-c3', '', [metric('p0-c3-i1', 'sentiment-score')], 3, 2),
    card('p0-c4', '', [metric('p0-c4-i1', 'total-reviews')], 3, 2),
    card('p0-c5', 'Trend Analysis', [chart('p0-c5-i1', 'trend-analysis', 'line')], 8, 5),
    card('p0-c6', 'Net Promoter Score', [chart('p0-c6-i1', 'net-promoter-score', 'line')], 4, 5),
  ],
  [
    card(
      'p1-c1',
      'Primary Service Ratings',
      [chart('p1-c1-i1', 'service-ratings', 'bar-horizontal')],
      12,
      5,
    ),
    card(
      'p1-c2',
      'All Service Ratings',
      [chart('p1-c2-i1', 'service-ratings', 'bar-horizontal')],
      12,
      5,
    ),
  ],
  [
    card('p2-c1', '', [metric('p2-c1-i1', 'grr')], 3, 2),
    card('p2-c2', '', [metric('p2-c2-i1', 'online-review-rating')], 3, 2),
    card('p2-c3', '', [metric('p2-c3-i1', 'compset-position')], 3, 2),
    card('p2-c4', '', [metric('p2-c4-i1', 'nps-score')], 3, 2),
    card('p2-c5', 'Trend Analysis', [chart('p2-c5-i1', 'trend-analysis', 'area')], 8, 5),
    card('p2-c6', 'Competitor Performance', [chart('p2-c6-i1', 'competitor', 'bar')], 4, 5),
  ],
  [
    card('p3-c1', '', [metric('p3-c1-i1', 'grr')], 4, 2),
    card('p3-c2', '', [metric('p3-c2-i1', 'online-review-rating')], 4, 2),
    card('p3-c3', '', [metric('p3-c3-i1', 'compset-position')], 4, 2),
    card('p3-c4', 'Trend Analysis', [chart('p3-c4-i1', 'trend-analysis', 'area')], 8, 5),
    card('p3-c5', 'Review Source Volume', [chart('p3-c5-i1', 'review-source-volume', 'bar')], 4, 5),
  ],
  [
    card('p4-c1', '', [metric('p4-c1-i1', 'nps-score')], 3, 2),
    card('p4-c2', '', [metric('p4-c2-i1', 'sentiment-score')], 3, 2),
    card('p4-c3', '', [metric('p4-c3-i1', 'total-reviews')], 3, 2),
    card('p4-c4', '', [metric('p4-c4-i1', 'response-rate')], 3, 2),
    card('p4-c5', 'Net Promoter Score', [chart('p4-c5-i1', 'net-promoter-score', 'line')], 8, 4),
    card('p4-c6', 'Sentiment', [chart('p4-c6-i1', 'sentiment', 'donut')], 4, 4),
  ],
  [
    card('p5-c1', '', [metric('p5-c1-i1', 'total-reviews')], 6, 2),
    card('p5-c2', '', [metric('p5-c2-i1', 'response-rate')], 6, 2),
    card('p5-c3', 'Review Source Volume', [chart('p5-c3-i1', 'review-source-volume', 'bar')], 6, 5),
    card('p5-c4', 'Trend Analysis', [chart('p5-c4-i1', 'trend-analysis', 'line')], 6, 5),
  ],
];

const SEED_DATE = new Date('2026-05-21T00:00:00Z');

export const PREDEFINED_REPORTS: Report[] = [
  {
    id: 'predefined-ai-analytics',
    name: 'AI Analytics',
    description: 'AI-powered guest analytics and predictive insights',
    type: 'predefined',
    format: 'report',
    createdAt: SEED_DATE,
    updatedAt: SEED_DATE,
    createdBy: 'system',
    isPublic: true,
    tags: [DEFAULT_TAG],
    widgets: PREDEFINED_LAYOUTS[0],
  },
  {
    id: 'predefined-service-rating',
    name: 'Service Rating',
    description: 'Service quality scores, ticket resolution and sentiment trends',
    type: 'predefined',
    format: 'report',
    createdAt: SEED_DATE,
    updatedAt: SEED_DATE,
    createdBy: 'system',
    isPublic: true,
    tags: [DEFAULT_TAG],
    widgets: PREDEFINED_LAYOUTS[1],
  },
  {
    id: 'predefined-comprehensive',
    name: 'Comprehensive',
    description: 'Full-spectrum overview of all key performance metrics',
    type: 'predefined',
    format: 'report',
    createdAt: SEED_DATE,
    updatedAt: SEED_DATE,
    createdBy: 'system',
    isPublic: true,
    tags: [DEFAULT_TAG],
    widgets: PREDEFINED_LAYOUTS[2],
  },
  {
    id: 'predefined-trend',
    name: 'Trend',
    description: 'Trend analysis across review ratings, occupancy and competitor data',
    type: 'predefined',
    format: 'report',
    createdAt: SEED_DATE,
    updatedAt: SEED_DATE,
    createdBy: 'system',
    isPublic: true,
    tags: [DEFAULT_TAG],
    widgets: PREDEFINED_LAYOUTS[3],
  },
  {
    id: 'predefined-sentiment',
    name: 'Sentiment',
    description: 'Guest sentiment, NPS and review sentiment breakdown',
    type: 'predefined',
    format: 'report',
    createdAt: SEED_DATE,
    updatedAt: SEED_DATE,
    createdBy: 'system',
    isPublic: true,
    tags: [DEFAULT_TAG],
    widgets: PREDEFINED_LAYOUTS[4],
  },
  {
    id: 'predefined-review-source',
    name: 'Review Source',
    description: 'Review volume distribution by source channel',
    type: 'predefined',
    format: 'dashboard',
    createdAt: SEED_DATE,
    updatedAt: SEED_DATE,
    createdBy: 'system',
    isPublic: true,
    tags: [DEFAULT_TAG],
    widgets: PREDEFINED_LAYOUTS[5],
  },
];

function buildSeedReports(count: number): Report[] {
  const reports: Report[] = [];

  for (let i = 0; i < count; i++) {
    const today = new Date('2026-05-21T00:00:00Z');
    const createdDaysAgo = Math.floor(Math.random() * 90);
    const createdDate = new Date(today);
    createdDate.setUTCDate(today.getUTCDate() - createdDaysAgo);

    reports.push({
      id: `report-${i + 1}`,
      name: REPORT_NAMES[i % REPORT_NAMES.length],
      description: REPORT_DESCRIPTIONS[i % REPORT_DESCRIPTIONS.length],
      type: 'custom',
      format: i % 4 === 0 ? 'dashboard' : 'report',
      createdAt: createdDate,
      updatedAt: createdDate,
      createdBy: `user-${(i % 5) + 1}`,
      isPublic: i % 3 !== 0,
      tags: REPORT_TAGS[i % REPORT_TAGS.length],
      widgets: REPORT_WIDGET_LAYOUTS[i % REPORT_WIDGET_LAYOUTS.length],
    });
  }

  return reports;
}

export const REPORTS_SEED: readonly Report[] = [...PREDEFINED_REPORTS, ...buildSeedReports(12)];
